import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sun, Moon, MoonStar, Home, Wallet, Store, ShoppingBag,
  ArrowLeftRight, FileText, Sparkles, Settings, LogOut, CreditCard, User, ChevronDown
} from 'lucide-react';
import api from '../../utils/api';

const MenuItem = ({ icon: Icon, label, onClick, customIcon }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-4 w-full p-3 hover:bg-gray-100 dark:hover:bg-gray-900 dim:hover:bg-gray-800 transition-colors rounded-md text-lg font-bold"
  >
    {customIcon ? (
      <span className="w-6 h-6 text-black dark:text-white dim:text-gray-300 flex items-center justify-center">
        {customIcon}
      </span>
    ) : (
      <Icon className="w-6 h-6 text-black dark:text-white dim:text-gray-300" />
    )}
    <span className="text-black dark:text-white dim:text-gray-200">{label}</span>
  </button>
);

const SideDrawer = ({ isOpen, onClose, theme, toggleTheme, currentUser, onLogout, accountType, onToggleAccountType }) => {
  const navigate = useNavigate();

  if (!currentUser) return null;

  const handleNav = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[500]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 bottom-0 w-[80%] max-w-[320px] bg-white dark:bg-black dim:bg-[#1a1a1a] border-r border-gray-200 dark:border-gray-800 dim:border-gray-800 z-[501] flex flex-col overflow-y-auto transition-colors duration-200"
          >
            {/* Header: User Info */}
            <div className="p-5 pb-3">
              <div className="flex justify-between items-start mb-2">
                <img
                  src={currentUser?.company_logo || "/assets/default-avatar.png"}
                  alt="Profile"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="relative group">
                  <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-900 dim:hover:bg-gray-800 transition-colors">
                    {theme === 'light' && <Sun className="w-5 h-5 text-amber-500" />}
                    {theme === 'dim' && <MoonStar className="w-5 h-5 text-blue-300" />}
                    {theme === 'dark' && <Moon className="w-5 h-5 text-indigo-400" />}
                  </button>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-black text-white rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    {theme === 'light' ? 'Switch to Dim' : theme === 'dim' ? 'Switch to Dark' : 'Switch to Light'}
                  </span>
                </div>
              </div>
              <div className="font-bold text-lg text-black dark:text-white dim:text-gray-200">
                {currentUser?.company_name || currentUser?.fullname}
              </div>
              <div className="text-gray-500 text-sm mb-4 dim:text-gray-400">
                {currentUser?.username}
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-3">
              {/* Account Type Toggle */}
              <div className="px-3 mb-4 mt-3">
                <div
                  className="bg-gray-200 dark:bg-gray-900 dim:bg-gray-800 p-1 rounded-full flex relative border border-gray-300 dark:border-gray-800 dim:border-gray-700 select-none shadow-inner w-full"
                >
                  <motion.div
                    className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-white dark:bg-gray-700/80 dim:bg-gray-700 rounded-full shadow-sm z-0"
                    animate={{ x: accountType === 'personal' ? 0 : '100%' }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                  <div
                    onClick={() => onToggleAccountType('personal')}
                    className={`relative z-10 flex-1 pt-4 text-sm font-bold transition-colors text-center cursor-pointer ${accountType === 'personal' ? 'text-black dark:text-white dim:text-gray-200' : 'text-gray-500 dim:text-gray-400'}`}
                  >
                    Personal
                  </div>
                  <div
                    onClick={() => onToggleAccountType('business')}
                    className={`relative z-10 flex-1 pt-4 text-sm font-bold transition-colors text-center cursor-pointer ${accountType === 'business' ? 'text-black dark:text-white dim:text-gray-200' : 'text-gray-500 dim:text-gray-400'}`}
                  >
                    Business
                  </div>
                </div>
              </div>

              <MenuItem icon={Home} label="Home" onClick={() => handleNav('/dashboard')} />
              <MenuItem icon={Wallet} label="Wallet" onClick={() => handleNav('/wallet')} />
              <MenuItem icon={Store} label="Store" onClick={() => handleNav('/store')} />
              <MenuItem icon={ShoppingBag} label="Orders" onClick={() => handleNav('/orders')} />
              <MenuItem icon={ArrowLeftRight} label="Transactions" onClick={() => handleNav('/transactions')} />
              <MenuItem icon={FileText} label="Invoices" onClick={() => handleNav('/invoices')} />
              <MenuItem icon={Sparkles} label="Meo AI" onClick={() => handleNav('/settings#meo-voice')} />
              <MenuItem icon={User} label="Profile" onClick={() => handleNav('/settings')} />
              <MenuItem icon={CreditCard} label="Billing" onClick={() => handleNav('/settings#bills')} />
            </div>

            <div className="h-[1px] bg-gray-200 dark:bg-gray-800 dim:bg-gray-800 mx-5 my-2"></div>

            {/* Footer */}
            <div className="p-3 pb-5">
              <button
                onClick={() => handleNav('/settings')}
                className="flex items-center justify-between w-full p-4 hover:bg-gray-100 dark:hover:bg-gray-900 dim:hover:bg-gray-800 rounded-md text-sm sm:text-base font-medium text-black dark:text-white dim:text-gray-200"
              >
                <span className="whitespace-nowrap">Settings & Support</span>
                <ChevronDown className="w-4 h-4 -rotate-90 text-gray-400 dim:text-gray-500" />
              </button>

              <div className="mt-2 px-2">
                <button
                  onClick={() => { onLogout(); onClose(); }}
                  className="flex items-center justify-start w-full py-2 font-bold rounded-full border border-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors px-4"
                >
                  <LogOut className="w-5 h-5 mr-3" /> Log out
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SideDrawer;
